// C:\Helpdesk\frontend\src\pages\admin\OrganizationForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, Checkbox, Row, Col } from 'antd'; // Removed 'message' import
import api from '../../utils/api'; 
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const { Option } = Select;
const { TextArea } = Input; 

const OrganizationForm = ({ visible, onCancel, onSuccess, organization }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key); 
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5, 
        placement: 'topRight',
        style: {
          width: 400, 
          zIndex: 10000, 
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({ 
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3, 
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000, 
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; 
    const minDisplayTime = 500; 

    const startTime = Date.now(); 
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0, 
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000, 
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer); 
      const elapsed = Date.now() - startTime; 
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) { 
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  useEffect(() => {
    if (!visible) {
      form.resetFields(); 
      return;
    }

    if (organization) {
      console.log('OrganizationForm: Initializing form for editing organization:', organization);
      // Backend mapOrganization returns camelCase, so use camelCase here
      form.setFieldsValue({
        name: organization.name,
        type: organization.type,
        email: organization.email,
        phoneNumber: organization.phoneNumber, // Backend maps phone_number to phoneNumber
        faxNumber: organization.faxNumber,     // Backend maps fax_number to faxNumber
        address: organization.address, 
        websiteUrl: organization.websiteUrl,   // Backend maps website_url to websiteUrl
        isActive: Boolean(organization.isActive), // Backend maps is_active to isActive
      });
    } else {
      console.log('OrganizationForm: Resetting form fields for new organization.');
      form.resetFields();
      // Use isActive for initial value as per backend expectation
      form.setFieldsValue({ 
          type: 'customer', 
          isActive: true // Changed from is_active to isActive
      }); 
    }
  }, [visible, organization, form]); 

  const handleSubmit = async () => {
    let hideLoading; // Declare hideLoading outside try block
    try {
      setLoading(true);
      const loadingKey = 'orgSubmitLoading';
      hideLoading = showLoading(loadingKey, organization ? 'Updating organization...' : 'Creating organization...'); 

      const values = await form.validateFields();
      console.log('OrganizationForm: Form values validated:', values);
      
      const payload = {
        name: values.name,
        type: values.type,
        email: values.email || null, 
        phoneNumber: values.phoneNumber || null, // Ensure camelCase for payload
        faxNumber: values.faxNumber || null,     // Ensure camelCase for payload
        address: values.address || null, 
        websiteUrl: values.websiteUrl || null,   // Ensure camelCase for payload
        isActive: values.isActive,               // Ensure camelCase for payload
      };

      if (organization) {
        console.log('OrganizationForm: Attempting to update organization:', organization.id, payload);
        await api.put(`/api/organizations/${organization.id}`, payload); 
        showSuccess('Organization Updated', 'Organization details updated successfully!', 'orgUpdateSuccess');
      } else {
        console.log('OrganizationForm: Attempting to create new organization:', payload);
        await api.post('/api/organizations', payload); 
        showSuccess('Organization Created', 'New organization created successfully!', 'orgCreateSuccess');
      }

      // Introduce a small delay before closing the modal and dismissing loading
      // This gives the notification component time to render the success message.
      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading(); // Dismiss loading notification
        onSuccess(true); // Pass true to indicate it was triggered by an action
        onCancel(); 
      }, 500); 

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading(); // Dismiss loading on error
      console.error('OrganizationForm: Form submission error:', error.response?.data || error.message, error);
      const errorMessage = error.response?.data?.message || 'Failed to save organization.';
      showError('Submission Failed', errorMessage, 'orgFormSubmitError');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible} 
      title={organization ? 'Edit Organization' : 'Add New Organization'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}> {/* Disable cancel button while loading */}
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700} 
    >
      {/* No contextHolderNotification needed here, it's global in App.js */}
      <Form
        form={form}
        layout="vertical"
        name="organization_form"
        // initialValues now consistently uses isActive
        initialValues={!organization ? { type: 'customer', isActive: true } : {}} 
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Organization Name"
              rules={[{ required: true, message: 'Please input organization name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Organization Type"
              rules={[{ required: true, message: 'Please select organization type' }]}
            >
              <Select placeholder="Select type">
                <Option value="customer">Customer</Option>
                <Option value="partner">Partner</Option>
                <Option value="internal">Internal</Option> 
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phoneNumber"
              label="Phone Number"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="faxNumber"
              label="Fax Number"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
                name="websiteUrl"
                label="Website URL"
                rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
            >
                <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
            name="address"
            label="Address"
        >
            <TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="isActive" // Use isActive
          label="Status"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrganizationForm;